import React, { Fragment, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { connect } from 'react-redux';

import { Maybe } from 'types/graphql-api.generated';

import { TheaterReservationTracking } from 'common/constants/trackingEventsNames';
import { removeClass } from 'common/tools/dom/classes';
import trans from 'common/tools/translations/trans';
import { ReduxUser } from 'common/types';

import Pager from 'website/components/Pager';
import RollerDate from 'website/components/RollerDate/';
import TheaterCard from 'website/components/TheaterCard';
import PageFilters from 'website/components/showtimes/PageFilters';
import ShowtimesAlertMsg from 'website/components/showtimes/ShowtimesAlertMsg';
import ShowtimesTitle from 'website/components/showtimes/ShowtimesTitle';
import ShowtimesVersionHolder from 'website/components/showtimes/ShowtimesVersionHolder';
import AuthenticatedLink from 'website/components/user/AuthenticatedLink';
import allocineContainer from 'website/containers/allocineContainer';
import {
  MovieFromAttributes,
  OnDateChange,
  RequestFavoriteData
} from 'website/containers/showtimes/types';
import { LOADING_CLASS } from 'website/containers/showtimes/utils/constants';
import {
  initializeCommonData,
  RequestFilters
} from 'website/containers/showtimes/utils/initializationUtils';
import {
  RequestPayload,
  ShowtimesFiltersFacets
} from 'website/containers/showtimes/utils/types';
import {
  useDateChange,
  useFetchShowtimes,
  useFilterChange,
  usePageChange
} from 'website/containers/showtimes/utils/useShowtimesHooks';
import { waitForLoginStatus } from 'website/containers/user/connection';
import theaterFavorites from 'website/containers/user/theaterFavorites';
import { Dispatch, ReduxConnectDelegatedProps } from 'website/reducers';
import { openShowtimeModal } from 'website/services/modal';

import { getDisabledDates, formatForApi, requestShowtimes } from '../utils';

type TheatersShowtimeUiProps = {
  dispatch: Dispatch;
  movie?: MovieFromAttributes;
  onDateChange: OnDateChange;
  theatersShowtimesData?: RequestFavoriteData;
  user?: ReduxUser | null;
};

type ShowtimesUiProps = {
  anchor: HTMLElement;
  currentDay?: string;
  filters?: Maybe<ShowtimesFiltersFacets[]>;
  movie: MovieFromAttributes;
  theaters: string[];
  user?: Maybe<ReduxUser>;
  versionDisabled?: boolean;
};

/* GLOBAL VARS */
const SHOWTIME_API_ROUTE = 'internal_api_showtimes_my_theaters_filtered';
const SHOWTIMES_LOCAL_ROUTE = 'moviepage_showtimes_my_theaters';

/* UI INTERMEDIATE COMPONENTS */
const LogInButton = () => (
  <AuthenticatedLink className="search-theater-link blue-link">
    {trans('homepage_movie.theaters-logged-out')}
  </AuthenticatedLink>
);

const TheatersShowtimeUi = ({
  dispatch,
  movie,
  onDateChange,
  theatersShowtimesData,
  user = null
}: TheatersShowtimeUiProps) => {
  if (
    !theatersShowtimesData ||
    (theatersShowtimesData.error && theatersShowtimesData.message) ||
    !theatersShowtimesData.results?.totalCount
  ) {
    return (
      <ShowtimesAlertMsg
        data={theatersShowtimesData}
        onDateChange={onDateChange}
      />
    );
  }

  return (
    <div className="showtimes-list-holder">
      {theatersShowtimesData.results.edges?.map(item => {
        const node = item?.node;
        if (!node) {
          return null;
        }

        const { theater, dubbed, local, original } = node;

        const trackingObj: Omit<TheaterReservationTracking, 'showtime'> = {
          movie,
          theater
        };

        return (
          movie &&
          theater && (
            <TheaterCard
              theater={theater}
              key={theater?.internalId}
              user={user}
              dispatch={dispatch}
            >
              <ShowtimesVersionHolder
                openModal={(showtimeId, fallback, trackingContext) =>
                  openShowtimeModal({
                    fallback,
                    showtimeId,
                    trackingContext
                  })
                }
                showtimes={{ dubbed, local, original }}
                theaterHasBooking={theater?.flags?.hasBooking}
                tracking={trackingObj}
              />
            </TheaterCard>
          )
        );
      })}
    </div>
  );
};

export { TheatersShowtimeUi as TheatersShowtimeUiForTest };

/* UI FINAL RENDER COMPONENT */
const ShowtimesUi = ({
  anchor,
  currentDay,
  filters: filtersFromProps,
  dispatch,
  movie,
  theaters,
  user,
  versionDisabled = false
}: ShowtimesUiProps & ReduxConnectDelegatedProps) => {
  const { data, payload, routeParam, setPayload, setRouteParam, totalPages } =
    useFetchShowtimes<RequestFavoriteData, RequestPayload>(
      anchor,
      {
        theaters: theaters,
        filters: formatForApi(filtersFromProps)
      },
      {
        movie: movie?.internalId,
        day: currentDay,
        page: 1
      },
      SHOWTIME_API_ROUTE
    );

  const { filters, handleFilterChange } = useFilterChange(
    data,
    filtersFromProps,
    setPayload,
    setRouteParam
  );
  const { handlePageChange, handlePagerUrl, selectedPage } = usePageChange(
    anchor,
    1,
    payload,
    SHOWTIMES_LOCAL_ROUTE,
    routeParam,
    setRouteParam
  );
  const { handleAlertDateChange, handleDateChange, selectedDate } =
    useDateChange(currentDay, data?.rollerDates, setRouteParam);

  // INTERMEDIATE COMPONENT RENDER
  const renderShowtimeTitle = () => <ShowtimesTitle data={data} />;

  const renderFilters = () => (
    <PageFilters
      filters={filters}
      onFilterChange={handleFilterChange}
      seeMore
      versionDisabled={versionDisabled}
      tracking={{ movie, filters }}
    />
  );

  const renderRoller = useCallback(() => {
    const disabledDates = getDisabledDates(
      data?.rollerDates,
      data?.showtimesDates
    );

    return (
      <RollerDate
        dates={data?.rollerDates}
        disabledDates={disabledDates}
        selectedDate={selectedDate}
        onClick={index => handleDateChange(data?.rollerDates?.[index])}
        tracking={{ movie }}
      />
    );
  }, [
    data?.rollerDates,
    data?.showtimesDates,
    handleDateChange,
    movie,
    selectedDate
  ]);

  const renderTheaters = () => (
    <TheatersShowtimeUi
      movie={movie}
      theatersShowtimesData={data}
      user={user}
      dispatch={dispatch}
      onDateChange={handleAlertDateChange}
    />
  );

  const renderPager = () => (
    <Pager
      currentPage={selectedPage}
      pageCount={totalPages}
      includesFirst
      includesLast
      getPageUrl={handlePagerUrl}
      onNavigationChange={handlePageChange}
    />
  );

  // FINAL UI COMPONENT RENDER

  return (
    <Fragment>
      {renderFilters()}
      {renderRoller()}
      {renderShowtimeTitle()}
      {renderTheaters()}
      {renderPager()}
    </Fragment>
  );
};

export { ShowtimesUi as ShowtimesUiForTest };

/* EXECUTION */
const mapStateToProps = (state: { user: ReduxUser }) => ({
  user: state.user
});

const ConnectedShowtimesUi = allocineContainer<ShowtimesUiProps>(
  connect(mapStateToProps)(ShowtimesUi)
);

const ConnectedLogInButton = allocineContainer(
  connect(mapStateToProps)(LogInButton)
);

export const init = async (anchor?: HTMLElement) => {
  if (!anchor) {
    return;
  }

  const user = await waitForLoginStatus();

  if (!user.loggedIn) {
    const root = createRoot(anchor);
    root.render(<ConnectedLogInButton />);
    return;
  }

  const myTheaters = await theaterFavorites();

  const requestFilters: RequestFilters = async ({ movie, currentDay }) =>
    await requestShowtimes<RequestFavoriteData, RequestPayload>(
      SHOWTIME_API_ROUTE,
      { movie: movie?.internalId, day: currentDay },
      { theaters: myTheaters }
    );

  const { currentDay, filters, movie, versionDisabled } =
    await initializeCommonData(anchor, requestFilters);

  if (!movie) {
    return;
  }

  let root;

  try {
    root = createRoot(anchor);
    root.render(
      <ConnectedShowtimesUi
        anchor={anchor}
        filters={filters}
        currentDay={currentDay}
        movie={movie}
        theaters={myTheaters === false ? [] : myTheaters}
        versionDisabled={versionDisabled}
      />
    );
  } finally {
    removeClass(anchor, LOADING_CLASS);
  }
  return root;
};
